import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { IExpense2, IUser } from "../types/types";
import { EXHUT_WEB_LOCAL_STORAGE_TOKEN } from "../utils/constants";

interface ICreateIncomePayload {
  // Always the annual amount. The income form converts
  // monthly / yearly / hourly input into an annual figure.
  amount: number;
  name?: string;
  users: string[];
}

export function useCreateIncome(groupId?: string) {
  return _usePost<any, any, ICreateIncomePayload>({
    path: `groups/${groupId}/incomes`,
    keys: ["groups", groupId!, "incomes"],
  });
}

interface ICreateExpensePayload {
  category: string;
  subcategory: string;
  amount: number;
  expenseDate: string;
  creditcard: string;
  comments: string;
}

export function useCreateMultiExpense(groupId?: string) {
  return _usePost<IExpense2[], any, ICreateExpensePayload[]>({
    path: `groups/${groupId}/expenses`,
    keys: ["expenses", groupId!],
  });
}

interface ICreateTripExpensePayload {
  amount: number;
  expenseDate: string;
  comments: string;
  type: "trip";
}

export function useCreateTripExpense(groupId?: string, tripId?: string) {
  return _usePost<any, any, ICreateTripExpensePayload>({
    path: `groups/${groupId}/trips/${tripId}/expenses`,
    keys: ["groups", groupId!, "trips", tripId!, "expenses"],
  });
}

interface ICreateTripPayload {
  destination?: string;
  fromDate: string;
  name: string;
  toDate: string;
}

export function useCreateTrip(groupId?: string) {
  return _usePost<any, any, ICreateTripPayload>({
    path: `groups/${groupId}/trips`,
    keys: ["groups", groupId!, "trips"],
  });
}

interface ICreateSavingsGoalPayload {
  name: string;
  targetAmount: number;
  targetDate: string;
  users?: string[];
}

export function useCreateSavingsGoal(groupId?: string) {
  return _usePost<any, any, ICreateSavingsGoalPayload>({
    path: `groups/${groupId}/savings-goals`,
    keys: ["groups", groupId!, "savings-goals"],
  });
}

interface ICreateSavingsGoalItemPayload {
  amount: number;
  itemDate: string;
  note?: string;
}

export function useCreateSavingsGoalItem(groupId?: string, goalId?: string) {
  return _usePost<any, any, ICreateSavingsGoalItemPayload>({
    path: `groups/${groupId}/savings-goals/${goalId}/items`,
    keys: ["groups", groupId!, "savings-goals", goalId!, "items"],
  });
}

export function useCreateAccount() {
  return _usePost<any, any, { name: string }>({
    path: `accounts`,
    keys: ["accounts"],
  });
}

interface ICreateMiscExpensePayload {
  amount: number;
  expenseDate: string;
  comments: string;
  type: "misc";
}

export function useCreateMiscExpense(groupId?: string) {
  return _usePost<any, any, ICreateMiscExpensePayload>({
    path: `groups/${groupId}/expenses/misc`,
    keys: ["expenses"],
  });
}

export function useRemoveUserToGroup2() {
  return _usePost<any, any, { member: string; groupId: string }>({
    path: `groups/:groupId/removeUser`,
    keys: ["groups", "users"],
    replacePathParams: "groupId",
  });
}

export function useLeaveGroup2() {
  return _usePost<any, any, { groupId: string }>({
    path: `groups/:groupId/leave`,
    keys: ["groups", "users"],
    replacePathParams: "groupId",
  });
}

export function useAddUserToGroup2() {
  return _usePost<any, any, { memberUsername: string; groupId: string }>({
    path: `groups/:groupId/addUser`,
    keys: ["groups", "users"],
    replacePathParams: "groupId",
  });
}

interface ICreatePlaidAccountPayload {
  token: string;
  groupId: string;
  bankName: string;
  bankId?: string;
}

export function usePlaidCompleteConnect() {
  return _usePost<any, any, ICreatePlaidAccountPayload>({
    path: `accounts/complete-connect`,
    keys: ["accounts"],
  });
}

export function usePlaidCompleteReconnect(accountId?: string) {
  return _usePost<any, any, ICreatePlaidAccountPayload>({
    path: `accounts/${accountId}/complete-reconnect`,
    keys: ["accounts", accountId!, "complete-reconnect"],
  });
}

interface ICreatePlaidLinkSessionResponse {
  expiration: string;
  link_token: string;
  request_id: string;
}

export function useCreatePlaidLinkSession() {
  return _usePost<ICreatePlaidLinkSessionResponse, any, any>({
    path: `accounts/init-connect`,
    keys: ["accounts", "init-connect"],
  });
}

export function useReConnectPlaidLinkSession(id?: string) {
  return _usePost<ICreatePlaidLinkSessionResponse, any, any>({
    path: `accounts/${id}/reconnect`,
    keys: ["accounts", id!, "reconnect"],
  });
}

export function useDisconnectPlaidLink(id?: string) {
  return _usePost<ICreatePlaidLinkSessionResponse, any, any>({
    path: `accounts/${id}/disconnect`,
    keys: ["accounts", id!, "disconnect"],
  });
}

interface ICreateLedgerExpensePayload {
  amount: number;
  expenseDate: string;
  comments: string;
  type: "ledger";
}

export function useCreateLedgerExpense(groupId?: string, id?: string) {
  return _usePost<any, any, ICreateLedgerExpensePayload>({
    path: `groups/${groupId}/ledgers/${id}/transactions`,
    keys: ["groups", groupId!, "ledgers", id!, "transactions"],
  });
}

interface ICreateSubscriptionPayload {
  firstName: string;
  lastName: string;
  countryCode: "USA";
  street1: string;
  amount: number;
  city: string;
  state: string;
  zipCode: string;
  cardToken: string;
  cardExpiry: string;
  tax: number;
  total: number;
  phone: string;
  email: string;
  authConsent: string;
  monthlyCycleDate: string;
  subscriptionPlanId: string;
  subscriptionPlanName: string;
  nonce: string;
}

export function useCreateSubscription() {
  return _usePost<any, any, ICreateSubscriptionPayload>({
    path: `subscriptions`,
    keys: ["subscriptions"],
  });
}

interface ICreateLedgerPayload {
  name: string;
  currency: string;
  shared: boolean;
}

export function useCreateLedger(groupId?: string) {
  return _usePost<any, any, ICreateLedgerPayload>({
    path: `groups/${groupId}/ledgers`,
    keys: ["groups", groupId!, "ledgers"],
  });
}

interface ICreateSavingsPayload2 {
  description?: string;
  amount?: number;
  savingsDate: string;
  shared?: boolean;
  users?: string[];
}

export function useCreateSavings2(groupId?: string) {
  return _usePost<any, any, ICreateSavingsPayload2>({
    path: `groups/${groupId}/savings`,
    keys: ["groups", groupId!, "savings"],
  });
}

interface IMoveExpenseToGroup {
  newGroupId: string;
}

export function useMoveExpenseToGroup(groupId?: string, expenseId?: string) {
  return _usePost<any, any, IMoveExpenseToGroup>({
    path: `groups/${groupId}/expenses/${expenseId}/move/groups/:newGroupId`,
    keys: ["groups", groupId!, "expenses"],
    replacePathParams: "newGroupId",
  });
}

interface IMoveExpenseToMiscPayload {
  expenseId: string;
}

export function useMoveExpenseToMisc(groupId?: string, expenseId?: string) {
  return _usePost<any, any, IMoveExpenseToMiscPayload>({
    path: `groups/${groupId}/expenses/:expenseId/move/misc`,
    keys: ["miscExpenses"],
    replacePathParams: "expenseId",
    resetKeys: [],
  });
}

interface IMoveExpenseToLedger {
  ledgerId: string;
}

export function useMoveExpenseToLedger(groupId?: string, expenseId?: string) {
  return _usePost<any, any, IMoveExpenseToLedger>({
    path: `groups/${groupId}/expenses/${expenseId}/move/ledgers/:ledgerId`,
    keys: ["groups", groupId!, "expenses"],
    replacePathParams: "ledgerId",
  });
}

interface IMoveExpenseToTrip {
  tripId: string;
}

export function useMoveExpenseToTrip(groupId?: string, expenseId?: string) {
  return _usePost<any, any, IMoveExpenseToTrip>({
    path: `groups/${groupId}/expenses/${expenseId}/move/trips/:tripId`,
    keys: ["groups", groupId!, "expenses"],
    replacePathParams: "tripId",
  });
}

export interface IAuthenticatePayload {
  email: string;
  password: string;
}

export type IRegisterPayload = IAuthenticatePayload & {
  source: "expense-tracker";
};

export type IAuthenticatResponse = IUser;

export function useRegisterMutation() {
  return _usePostAuthBE<void, undefined, IRegisterPayload>(
    "register?source=app",
    ["register"],
    [""],
    false,
  );
}

export interface IRegisterSSOPayload {
  token: string;
}

export function registerWithSSO() {
  return _usePostAuthBE<IAuthenticatResponse, any, IRegisterSSOPayload>(
    "register/sso",
    ["register"],
    [],
    false,
  );
}

export function useAuthenticate() {
  return _usePostAuthBE<IAuthenticatResponse, undefined, IAuthenticatePayload>(
    "auth",
    [],
    [],
    false,
  );
}

export interface IRegisterVerifyPayload {
  cid: string;
  regkey: string;
}

export function useRegisterVerify() {
  return _usePostAuthBE<
    IAuthenticatResponse,
    undefined,
    IRegisterVerifyPayload
  >("register/verify", ["register", "verify"], [], false);
}

export interface IAuthenticateSSOPayload {
  token: string;
  type: "google";
}

export function useAuthenticateWithSSO() {
  return _usePostAuthBE<IAuthenticatResponse, null, IAuthenticateSSOPayload>(
    `auth/sso`,
    [],
    [],
  );
}

export function usePostLogout() {
  return _usePostAuthBE<void, null, void>("logout", [], []);
}

function _usePostAuthBE<T1, T2, T3>(
  path: string,
  keys: string[],
  resetKeys: string[],
  authenticated = true,
) {
  return _usePost<T1, T2, T3>({
    path,
    keys,
    resetKeys,
    authenticated,
    host: process.env.REACT_APP_AUTH_BE_URL,
  });
}

interface ICreateGroupPayload {
  name: string;
}

export function useCreateGroup() {
  return _usePost<any, any, ICreateGroupPayload>({
    path: `groups`,
    keys: ["groups"],
  });
}

function _usePost<T1, T2, T3>({
  path,
  keys,
  resetKeys,
  authenticated = true,
  host,
  replacePathParams,
}: {
  path: string;
  keys?: string[];
  resetKeys?: string[];
  authenticated?: boolean;
  host?: string;
  replacePathParams?: string;
}) {
  let _url = host
    ? `${host}/${[path]}`
    : `${process.env.REACT_APP_BE_URL2}/${path}`;

  let headers: any = {
    "Content-Type": "application/json",
  };

  if (authenticated) {
    const sign = localStorage.getItem(EXHUT_WEB_LOCAL_STORAGE_TOKEN);
    headers["Authorization"] = sign;
  } else {
    headers["Access-Type"] = "offline";
  }

  return useMutation<T1, T2, T3>({
    mutationFn: async (req) => {
      if (replacePathParams) {
        _url = _url.replace(
          `:${replacePathParams}`,
          (req as any)[replacePathParams],
        );
      }
      return axios
        .post(_url, req, {
          headers,
        })
        .then((resp) => resp.data);
    },
    mutationKey: keys,
  });
}

export interface IVerifySMSVerificationPayload {
  code: number;
  phone: string;
}

export function usePostVerifySMSVerification() {
  return _usePostAuthBE<void, any, IVerifySMSVerificationPayload>(
    `users/verifications/sms/verify`,
    ["users-verifications", "profile"],
    ["users-verifications", "profile"],
  );
}

export interface ITriggerSMSVerificationPayload {
  phone: string;
}

export function usePostTriggerSMSVerification() {
  return _usePostAuthBE<void, any, ITriggerSMSVerificationPayload>(
    `users/verifications/sms`,
    ["users-verifications", "profile"],
    ["users-verifications", "profile"],
  );
}
